import { useMemo, useState } from "react";

import { type GroupBuilding } from "@eisox/api/core-api";
import { Button, DropdownMenu, Switch, Typography } from "@eisox/design-system";
import { Map } from "@eisox/design-system/map";
import { ArrowRightIcon, OptionIcon } from "@eisox/icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";

import { authApi } from "~/api";
import { SuspendedAccountDialog } from "~/components/suspended-account-dialog";
import { BuildingMapMarker } from "~/features/buildings";
import { Building, findSubGroupsHousesByGroupId, MoveBuildingDialog } from "~/features/groups";
import { getPermissions } from "~/helpers/auth/auth.helper";
import { m } from "~/paraglide/messages";
import { useGroupContext } from "~/providers/group.provider";

export const Route = createFileRoute("/_authenticated/_no-building/groups/$groupId/buildings")({
  component: RouteComponent,
});

function RouteComponent() {
  const { groupId } = Route.useParams();

  const { data: roles } = useSuspenseQuery(authApi.queries.roles);

  const { group, groups } = useGroupContext(Route.id);

  const [displaySubGroupsBuildings, setDisplaySubGroupsBuildings] = useState(true);
  const [suspendedBuildingId, setSuspendedBuildingId] = useState<string | undefined>(undefined);
  const [buildingToMove, setBuildingToMove] = useState<GroupBuilding>();

  const buildings = useMemo(() => group.buildings, [group]);
  const subGroupsBuildings = useMemo(() => findSubGroupsHousesByGroupId([group], groupId), [group, groupId]);

  const handleCheckedChange = (checked: boolean | null) => setDisplaySubGroupsBuildings(!!checked);
  const handleSuspendedBuildingClick = () => {
    if (suspendedBuildingId) {
      setSuspendedBuildingId(undefined);
    }
  };

  const sortedBuildings = buildings.sort((a, b) => a.name.localeCompare(b.name));
  const sortedSubGroupBuildings = subGroupsBuildings.sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="min-h-0 gap-1.25 flex w-full flex-1 text-base">
      <div className="w-68.75 gap-1.25 flex flex-col">
        <div className="gap-1.25 flex flex-1 flex-col overflow-auto">
          {buildings.length === 0 && (
            <Typography className="shrink-0 text-center italic">{m.no_building_associated()}</Typography>
          )}
          {sortedBuildings.map(building => (
            <Building.Root key={building.id}>
              <Building.Button asChild>
                <Link to={"/buildings/$buildingId"} params={{ buildingId: building.id }}>
                  <Typography>{building.name}</Typography>
                </Link>
              </Building.Button>
              {import.meta.env.VITE_GROUPS_ACTIONS_ENABLED && (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <Building.Action>
                      <OptionIcon />
                    </Building.Action>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end">
                    <DropdownMenu.Item asChild onSelect={() => setBuildingToMove(building)}>
                      <Button className="w-full text-primary-dark-blue">
                        {m.move()} <ArrowRightIcon />
                      </Button>
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              )}
            </Building.Root>
          ))}
          {subGroupsBuildings.length > 0 && displaySubGroupsBuildings && (
            <>
              <div className="my-2.5 w-full border-t border-primary-darker-gray" />
              {sortedSubGroupBuildings.map(building => (
                <Building.Root key={building.id} subgroup>
                  <Building.Button asChild>
                    <Link to={"/buildings/$buildingId/dashboard"} params={{ buildingId: building.id }}>
                      <Typography>{building.name}</Typography>
                    </Link>
                  </Building.Button>
                </Building.Root>
              ))}
            </>
          )}
        </div>
        {subGroupsBuildings.length > 0 && (
          <label className="gap-1.25 flex items-center justify-between italic">
            {m.sub_groups_buildings()}
            <Switch checked={displaySubGroupsBuildings} onCheckedChange={handleCheckedChange} />
          </label>
        )}
      </div>
      {/* key is used to force re-render the map when the groupId changes to zoom on markers */}
      <Map.Root key={groupId} className="flex-1 rounded-[10px]">
        <Map.MarkerClusterGroup>
          {buildings.map(b => {
            const { role } = getPermissions(roles, b.id);
            return <BuildingMapMarker key={b.id} building={b} role={role} onSuspendedClick={setSuspendedBuildingId} />;
          })}
          {displaySubGroupsBuildings &&
            subGroupsBuildings.map(b => {
              const { role } = getPermissions(roles, b.id);
              return (
                <BuildingMapMarker key={b.id} building={b} role={role} onSuspendedClick={setSuspendedBuildingId} />
              );
            })}
        </Map.MarkerClusterGroup>
      </Map.Root>
      {buildingToMove && (
        <MoveBuildingDialog
          open
          onOpenChange={() => setBuildingToMove(undefined)}
          building={buildingToMove}
          groups={groups}
          root={group.root}
        />
      )}
      <SuspendedAccountDialog
        open={!!suspendedBuildingId}
        onOpenChange={handleSuspendedBuildingClick}
        isAdmin={false}
      />
    </div>
  );
}
